import type {
  ColDef,
  ColumnMenuTab,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community';
import MassiveStatusRenderer from '@/app/common/components/CellRenderers/MassiveStatusRenderer';
import MassiveFilterRenderer from '@/app/common/components/CellRenderers/MassiveFilterRenderer';
import { decodeGetter } from '@/app/common/components/ag-grid/configs';
import { PauseMassiveContext, RemoveMassiveContext } from '@/context/Context';
import formatUtcDate from '@/helpers/formatUtcDate';
import { warningMessage } from '@/utils/toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useContext } from 'react';
import { Dropdown } from 'react-bootstrap';
import logo from '@/assets/img/icon-144x144.png';
import { BiLogoBing, BiLogoGoogle, BiLogoYoutube } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { getColor } from '@/helpers/utils';
import useDomain from '@/hooks/useDomain';
import type {
  MassiveHistoryItem,
  MassiveSearchType,
  MassiveSource
} from '@/app/KeywordResearch/types/massiveApiTypes';
import ClusterFromTaskMenuItem from '@/app/KeywordResearch/components/ClusterFromTaskMenuItem';

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

type MassiveHistorySource = MassiveSource | 'mizfa_tools';

type MassiveHistoryRow = MassiveHistoryItem & {
  source: MassiveHistorySource[];
  url?: unknown;
};

type MassiveHistoryColumn = ColDef<MassiveHistoryRow> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
};

type RemoveMassiveContextValue = {
  setShow: (show: boolean) => void;
  setTaskId: (taskId: string) => void;
  setkeyword: (keywords: string[]) => void;
};

type PauseMassiveContextValue = {
  setShowStopModal: (show: boolean) => void;
};

const searchTypeFormatter = (value: MassiveSearchType | string): string => {
  switch (value) {
    case 'nested':
      return 'تودرتو';

    case 'with_alphabet':
      return 'الفبا';

    case 'with_questions':
      return 'سوالی';

    case 'al_and_qu':
      return 'الفبا و سوالی';
    default:
      return '';
  }
};

export const massiveColumnConfig = (
  domain_slug: string
): MassiveHistoryColumn[] => [
  {
    field: 'keyword',
    type: 'string',
    title: 'کلمه',
    flex: 1,
    minWidth: 150,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<MassiveHistoryRow>) => {
      const keywords = Array.isArray(params.value) ? params.value : [];
      return (
        <Link
          to={`${params.data?.task_id}`}
          style={{
            color: getColor('navbar-vertical-default-link-active-color')
          }}
          className={`fw-bolder btn-link ${!params.data?.task_id && 'disabled-link'}`}
        >
          <div className="d-flex gap-1 align-items-baseline">
            {keywords.map(
              (item, i) =>
                decodeURIComponent(item).trim() +
                (keywords.length !== i + 1 ? '، ' : '')
            )}
          </div>
        </Link>
      );
    },
    filterValueGetter: decodeGetter
  },
  {
    field: 'status',
    type: 'number',
    title: 'وضعیت',
    width: 110,
    searchable: false,
    // filter: 'agTextColumnFilter',
    // floatingFilter: true,
    // menuTabs,
    cellRenderer: (params: ICellRendererParams<MassiveHistoryRow>) => (
      <MassiveStatusRenderer status={params.value} />
    )
  },
  {
    field: 'search_type',
    type: 'string',
    title: 'نوع',
    width: 75,
    searchable: false,
    // filter: 'agTextColumnFilter',
    floatingFilter: false,
    menuTabs,
    valueFormatter: (params: ValueFormatterParams<MassiveHistoryRow>) =>
      searchTypeFormatter(params.value),
    filterValueGetter: (params: ValueGetterParams<MassiveHistoryRow>) => {
      if (params.data) {
        const field = params.colDef.field as keyof MassiveHistoryRow | undefined;
        const value = field ? params.data[field] : undefined;
        return value ? searchTypeFormatter(String(value)) : '';
      }
      return '';
    }
  },
  {
    field: 'source',
    type: 'array',
    title: 'منبع',
    width: 77,
    searchable: false,
    // filter: 'agTextColumnFilter',
    floatingFilter: false,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<MassiveHistoryRow>) => (
      <div className="d-flex align-items-center h-100 gap-1">
        {params?.value?.includes('mizfa_tools') && (
          <img
            style={{
              filter: 'grayscale(100%)'
            }}
            src={logo}
            width={18}
            height={18}
            title="mizfa tools"
          />
        )}
        {params?.value?.includes('google') && (
          <BiLogoGoogle
            style={{ width: '18px', padding: 0, height: '18px', flexShrink: 0 }}
            size="18px"
            title="google"
          />
        )}
        {params?.value?.includes('youtube') && (
          <BiLogoYoutube
            style={{ width: '18px', padding: 0, height: '18px', flexShrink: 0 }}
            size="20px"
            title="youtube"
          />
        )}
        {params?.value?.includes('bing') && (
          <BiLogoBing
            style={{ width: '18px', padding: 0, height: '18px', flexShrink: 0 }}
            size="20px"
            title="bing"
          />
        )}
      </div>
    )
  },
  {
    field: 'ai_smart_filter',
    type: 'boolean',
    title: 'فیلتر',
    width: 85,
    searchable: false,
    floatingFilter: false,
    menuTabs,
    sortable: false,
    cellRenderer: (params: ICellRendererParams<MassiveHistoryRow>) => (
      <MassiveFilterRenderer data={params.data} />
    )
  },
  {
    field: 'count',
    type: 'number',
    title: 'تعداد',
    width: 75,
    searchable: false,
    // filter: 'agTextColumnFilter',
    floatingFilter: false,
    menuTabs
  },
  {
    field: 'inserted_at',
    type: 'string',
    title: 'تاریخ',
    width: 95,
    searchable: false,
    // filter: 'agTextColumnFilter',
    floatingFilter: false,
    menuTabs,
    valueFormatter: (params: ValueFormatterParams<MassiveHistoryRow>) =>
      formatUtcDate(params.value)
  },

  {
    field: 'url',
    type: 'array',
    title: ' ',
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 52,
    pinned: 'left',
    noFilter: true,
    cellRenderer: (params: ICellRendererParams<MassiveHistoryRow>) => {
      const { domain } = useDomain();

      const { setShow, setTaskId, setkeyword } = useContext(
        RemoveMassiveContext
      ) as RemoveMassiveContextValue;
      const { setShowStopModal } = useContext(
        PauseMassiveContext
      ) as PauseMassiveContextValue;

      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-center">
          <span className="allaho_akbar_zIndex">
            <Dropdown
              onClick={e => e.stopPropagation()}
              align="end"
              className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
            >
              <Dropdown.Toggle
                className="text-600 btn-reveal"
                split
                variant="reveal"
                size="sm"
              >
                <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
              </Dropdown.Toggle>
              <Dropdown.Menu className="border py-0">
                <Dropdown.Item
                  as={Link}
                  to={`/keyword-research/massive/${domain_slug}/${domain}/${params.data?.task_id}`}
                  className={classNames(
                    'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1'
                  )}
                >
                  <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                  مشاهده
                </Dropdown.Item>
                {(params?.data?.status === 2 || params?.data?.status === 4) && (
                  <Dropdown.Item
                    onClick={() => {
                      setShowStopModal(true);
                      setTaskId(params.data?.task_id ?? '');
                    }}
                    className={classNames(
                      'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1'
                    )}
                  >
                    <FontAwesomeIcon icon="pause" className="me-1 ms-2" />
                    متوقف کردن
                  </Dropdown.Item>
                )}
                <ClusterFromTaskMenuItem
                  source="massive"
                  taskId={params.data?.task_id}
                  keywordCount={params.data?.count}
                  isTaskReady={params.data?.status !== 3}
                />
                <Dropdown.Divider className="my-0" />
                <Dropdown.Item
                  onClick={() => {
                    if (params.data?.status === 4)
                      warningMessage(
                        'در حال جستجو هستی. لطفا صبر کن تا جستجو تمام بشه یا ابتدا جستجو رو متوقف کن'
                      );
                    else {
                      setTaskId(params.data?.task_id ?? '');
                      setkeyword(params.data?.keyword ?? []);
                      setShow(true);
                    }
                  }}
                  className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
                >
                  <FontAwesomeIcon
                    icon="trash-alt"
                    className="me-1 fs-0 ms-2"
                  />
                  حذف
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </span>
        </div>
      );
    }
  }
];
